import type { ScreenBase } from "@frui.ts/screens";
import { ConductorOneChildActive, Router } from "@frui.ts/screens";
import { computed } from "mobx";
import { Modules } from "config";
import LocalizationService from "services/localizationService";
import { bound } from "@frui.ts/helpers";
import RefundForRefundOfRefundsRepository from "repositories/refundForRefundOfRefundsRepository";
import ConfirmationService from "services/confirmationService";
import EventBus, {
  allRefundApprovalsApproved,
  approvedRefundsExported,
  blacklistedAccountsChanged,
  transferRefundForRefundOfRefundPaymentTypeChanged,
} from "services/eventBus";
import NotificationService, { NotificationType } from "services/notificationService";
import AppRouter from "services/router";
import SecurityService from "services/securityService";
import type RefundType from "entities/refundType";
import DomesticRefundForRefundOfRefundsDetailViewModel from "./transferRefundForRefundOfRefunds/domestic/domesticRefundForRefundOfRefundsDetailViewModel";
import DomesticRefundForRefundOfRefundsViewModel from "./transferRefundForRefundOfRefunds/domestic/domesticRefundForRefundOfRefundsViewModel";
import ForeignRefundForRefundOfRefundsDetailViewModel from "./transferRefundForRefundOfRefunds/foreign/foreignRefundForRefundOfRefundsDetailViewModel";
import ForeignRefundForRefundOfRefundsViewModel from "./transferRefundForRefundOfRefunds/foreign/foreignRefundForRefundOfRefundsViewModel";
import SEPARefundForRefundOfRefundsDetailViewModel from "./transferRefundForRefundOfRefunds/SEPA/SEPARefundForRefundOfRefundsDetailViewModel";
import SEPARefundForRefundOfRefundsViewModel from "./transferRefundForRefundOfRefunds/SEPA/SEPARefundForRefundOfRefundsViewModel";
import type TransferRefundForRefundOfRefundsViewModel from "./transferRefundForRefundOfRefunds/transferRefundForRefundOfRefundsViewModel";
import TransferPaymentType from "entities/transferPaymentType";

const navigationName = "refund-for-refund-of-refunds";

@Router.registerRoute({
  route: navigationName,
  name: Router.Self,
  children: [
    DomesticRefundForRefundOfRefundsViewModel,
    ForeignRefundForRefundOfRefundsViewModel,
    SEPARefundForRefundOfRefundsViewModel,
  ],
})
export default class RefundForRefundOfRefundsViewModel extends ConductorOneChildActive<TransferRefundForRefundOfRefundsViewModel> {
  private unsubscriptions: Array<() => void> = [];

  constructor(
    private repository: RefundForRefundOfRefundsRepository,
    private localizationService: LocalizationService,
    private domesticRefundForRefundOfRefundsViewModel: DomesticRefundForRefundOfRefundsViewModel,
    private foreignRefundForRefundOfRefundsViewModel: ForeignRefundForRefundOfRefundsViewModel,
    private SEPARefundForRefundOfRefundsViewModel: SEPARefundForRefundOfRefundsViewModel,
    private appRouter: AppRouter,
    protected confirmationService: ConfirmationService,
    protected notificationService: NotificationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService
  ) {
    super();
    this.navigationName = navigationName;
    this.children.push(
      this.domesticRefundForRefundOfRefundsViewModel,
      this.foreignRefundForRefundOfRefundsViewModel,
      this.SEPARefundForRefundOfRefundsViewModel
    );
  }

  protected async onInitialize() {
    if (!this.activeChild) {
      this.tryActivateChild(this.children[0]);
    }
    this.unsubscriptions.push(
      this.eventBus.subscribe(
        transferRefundForRefundOfRefundPaymentTypeChanged,
        this.onTransferRefundForRefundOfRefundPaymentTypeChanged
      ),
      this.eventBus.subscribe(blacklistedAccountsChanged, this.onBankAccountBlacklistsChanged)
    );
    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean): Promise<void> {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  closeChild(child: ScreenBase) {
    return false;
  }

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get canApproveAll() {
    return this.canProvideRefundApprovalAction;
  }

  @computed
  get canExportApproved() {
    return this.canProvideRefundApprovalAction && !this.noItemForExport;
  }

  @computed
  get noItemForExport() {
    return (
      this.domesticRefundForRefundOfRefundsViewModel.noApprovalsForExport &&
      this.foreignRefundForRefundOfRefundsViewModel.noApprovalsForExport &&
      this.SEPARefundForRefundOfRefundsViewModel.noApprovalsForExport
    );
  }

  @computed
  private get canProvideRefundApprovalAction() {
    return (
      this.securityService.allowTo("finance", "write") &&
      (this.securityService.allowTo("finance.factualauditing", "write") ||
        this.securityService.allowTo("finance.commandoperating", "write") ||
        this.securityService.allowTo("finance.chiefaccounting", "write"))
    );
  }

  @computed
  get canActionExportApprove() {
    return this.securityService.allowTo("finance", "write");
  }

  @bound
  async exportApprovedClick() {
    const confirmed = await this.confirmationService.showConfirmation(this.translate("confirmExportApprovedRefunds.title"), {
      confirmButton: { text: this.translate("confirmExportApprovedRefunds.confirm"), variant: "danger" },
    });

    if (confirmed) {
      try {
        await this.repository.exportApprovedRefunds();
        this.notificationService.add({ title: this.translate("refundsExported"), type: NotificationType.SUCCESS });
        this.eventBus.publish(allRefundApprovalsApproved());
      } catch (e) {
        // General error handle is good enough
      }
    }
  }

  @computed
  get approveAllButtonTitle() {
    const activeChild = this.activeChild;

    if (!activeChild) {
      return this.translate("approveAll").replace("%name%", "");
    }

    return this.translate("approveAll").replace("%name%", `${this.translateRefundTypeEnum(activeChild.refundType)}`);
  }

  @bound
  async approveAllClick() {
    const activeChild = this.activeChild;

    if (!activeChild) {
      return;
    }

    const confirmed = await this.confirmationService.showConfirmation(this.translate("confirmAllRefundsApproval.title"), {
      confirmButton: { text: this.translate("confirmAllRefundsApproval.confirm"), variant: "danger" },
    });

    if (confirmed) {
      try {
        await this.repository.approveRefunds(activeChild.refundType);
        this.notificationService.add({ title: this.translate("allApproved"), type: NotificationType.SUCCESS });
        this.eventBus.publish(approvedRefundsExported());
      } catch (e) {
        // General error handle is good enough
      }
    }
  }

  @bound
  private async onTransferRefundForRefundOfRefundPaymentTypeChanged(
    data: ReturnType<typeof transferRefundForRefundOfRefundPaymentTypeChanged>
  ) {
    const { refundForRefundOfRefundId, newTransferType } = data.payload;

    switch (newTransferType) {
      case TransferPaymentType.Domestic:
        await this.appRouter.navigate(DomesticRefundForRefundOfRefundsDetailViewModel, { refundForRefundOfRefundId });
        break;

      case TransferPaymentType.Sepa:
        await this.appRouter.navigate(SEPARefundForRefundOfRefundsDetailViewModel, { refundForRefundOfRefundId });
        break;

      case TransferPaymentType.Foreign:
        await this.appRouter.navigate(ForeignRefundForRefundOfRefundsDetailViewModel, { refundForRefundOfRefundId });
        break;
    }
  }

  @bound
  private onBankAccountBlacklistsChanged(data: ReturnType<typeof blacklistedAccountsChanged>) {
    this.children.forEach(children => {
      // Reload all non empty children lists - we have no data from BE to recognize by account numbers
      if (
        (children instanceof DomesticRefundForRefundOfRefundsViewModel ||
          children instanceof ForeignRefundForRefundOfRefundsViewModel ||
          children instanceof SEPARefundForRefundOfRefundsViewModel) &&
        children.isInitialized &&
        children.items.length
      ) {
        children.loadData();
      }
    });
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `refundForRefundOfRefunds.${code}`, size);
  }

  translateRefundTypeEnum(value: RefundType) {
    return this.localizationService.translateEnum("refundType", value);
  }
}
